import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Line,
  ComposedChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  LabelList,
} from 'recharts';

import { Box, CircularProgress, Divider, Typography } from '@mui/material';

import { truncateTo2dp } from 'utils/helpers/general';
import useAppTheme from 'hooks/useAppTheme';
import { useAppSelector } from 'store/hook';
import {
  useMemo,
  memo,
  type Dispatch,
  type SetStateAction,
  useState,
  useEffect,
} from 'react';
import {
  formatNumberToShortScale,
  maintenanceCostDistributionDataFormatter,
} from '../utils/dataFormatters';
import { HeaderCard } from './HeaderCard';
import { Circle } from '@mui/icons-material';
import SquaresDashboardHandler from 'handlers/squaresHandlers/squaresDashboard.handler';
import { adjustDateForLocalFormatting } from 'views/SurvivalAnalysis/Reliability';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { guessUserTimezone } from 'utils/helpers/dateTime';
import dayjs from 'dayjs';

import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

dayjs.extend(utc);
dayjs.extend(timezone);

const CustomTooltip = memo(
  ({
    active,
    payload,
    avgValue,
  }: {
    active?: boolean;
    payload?: any;
    avgValue: number;
  }) => {
    const { enableDarkTheme } = useAppTheme();
    const palette = enableDarkTheme
      ? clientThemePalette[CLIENT_THEME].dark
      : clientThemePalette[CLIENT_THEME].light;
    if (active && payload && payload.length) {
      const { name, cost, trend } = payload[0].payload;

      const styles = {
        container: {
          backgroundColor: enableDarkTheme
            ? 'rgba(30, 30, 30, 0.9)'
            : 'rgba(217, 229, 247, 0.8)',
          border: '1px solid #ccc',
          color: enableDarkTheme ? '#f1f1f1' : '#000',
          padding: '3px 5px',
          borderRadius: '4px',
        },
        text: { fontSize: '12px' },
      };

      return (
        <div style={styles.container}>
          <p style={styles.text}>{`Month: ${name}`}</p>
          <p style={styles.text}>{`Cost: $${cost}`}</p>
          <p style={styles.text}>
            {`Moving Monthly Average: $${formatNumberToShortScale(trend)}`}
          </p>
          <p style={styles.text}>
            {`Variance: ${truncateTo2dp((cost / avgValue) * 100)}%`}
          </p>
        </div>
      );
    }
    return null;
  }
);

CustomTooltip.displayName = 'CustomTooltip';

const CustomRowHighlight = memo(
  ({
    x,
    y,
    width,
    height,
    fill,
    payload,
    selectedCategory,
    chartWidth,
  }: any) => {
    const isSelected = payload.name === selectedCategory;
    const horizontalPadding = 2;
    return (
      <g>
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={fill}
          rx={4}
          cursor={'pointer'}
        />
        {isSelected && (
          <rect
            x={x - horizontalPadding} // shift highlight left
            y={y - 2}
            width={((chartWidth as number) || 0) + horizontalPadding * 2} // extend highlight width
            height={(height as number) + 4}
            fill="none"
            stroke="#FFA500"
            strokeWidth={2}
            rx={4}
            strokeDasharray="4 2"
          />
        )}
      </g>
    );
  }
);

CustomRowHighlight.displayName = 'CustomRowHighlight';

const CustomLegend = memo(() => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const legendItems = useMemo(
    () => [
      { label: t('dm_assign_actual'), color: palette.primary },
      { label: t('monthly_budget'), color: '#FFA403' },
      { label: t('rolling_avg_2'), color: '#E936EC' },
    ],
    [palette.primary]
  );

  return (
    <Box display="flex" gap={1}>
      {legendItems.map((item) => (
        <Box key={item.label} display="flex" alignItems="center" gap="2px">
          <Circle sx={{ fontSize: 14, color: item.color }} />
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 500,
              color: palette.textPrimary,
            }}
          >
            {item.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
});

CustomLegend.displayName = 'CustomLegend';

const getInitialDates = (timezoneBasedOnDisplayMode: string) => {
  const tz = timezoneBasedOnDisplayMode || guessUserTimezone();
  const initialStartDate = dayjs().tz(tz).subtract(1, 'year').utc().toDate();
  const initialEndDate = dayjs().tz(tz).endOf('day').utc().toDate();
  return { initialStartDate, initialEndDate };
};

export const MonthlyMaintenanceCostDistribution = ({
  selectedMonth,
  setSelectedMonth,
}: {
  selectedMonth: string | null;
  setSelectedMonth: Dispatch<SetStateAction<string | null>>;
}) => {
  const { t } = useTranslation();
  const squaresHandler = new SquaresDashboardHandler();
  const { timezoneBasedOnDisplayMode } = useTimeDisplayMode();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const [data, setData] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const { initialStartDate, initialEndDate } = useMemo(() => {
    return getInitialDates(timezoneBasedOnDisplayMode);
  }, [timezoneBasedOnDisplayMode]);

  const targetData = useAppSelector(
    (state) => state?.targetReducer?.data.squaresMaintenanceCost
  );

  const initialTargetValue =
    targetData?.length > 0
      ? targetData[targetData.length - 1]?.targetValue || 3000000
      : 3000000;

  // Centralized calcs
  const {
    chartMaxValue,
    chartData,
    targetedAverageMonthlyMaintenanceCost,
    totalCost,
  } = useMemo(
    () => maintenanceCostDistributionDataFormatter(data, initialTargetValue),
    [data, initialTargetValue]
  );

  // const getBarFill = useCallback(
  //   (entry: any) =>
  //     entry.cost > targetedAverageMonthlyMaintenanceCost
  //       ? '#FF3B30'
  //       : '#4A7BFA',
  //   [targetedAverageMonthlyMaintenanceCost]
  // );

  const handleBarClick = (data: any) => {
    if (data?.name) {
      setSelectedMonth(data?.name as string);
    }
  };

  const fetchData = async () => {
    try {
      const adjustedStartDate = adjustDateForLocalFormatting(
        initialStartDate,
        timezoneBasedOnDisplayMode
      );
      const adjustedEndDate = adjustDateForLocalFormatting(
        initialEndDate,
        timezoneBasedOnDisplayMode
      );

      const response = await squaresHandler.getPartsMaintenanceExpenditure(
        adjustedStartDate,
        adjustedEndDate,
        false
      );

      if (Array.isArray(response?.currentData)) {
        setData(response?.currentData);
      } else {
        throw new Error('Invalid response ');
      }
    } catch (error) {
      console.log(error);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void fetchData();
  }, []);

  return (
    <Box
      sx={{
        backgroundColor: enableDarkTheme
          ? palette.toolBarBackground
          : palette.toolBarBackground,
        borderRadius: '5px',
        border: enableDarkTheme ? '1px solid #2F445D' : '1px solid #BDBDBD',
      }}
      py={1}
      px={1}
      display={'flex'}
      flexDirection={'column'}
      gap={2}
      height={'420px'}
    >
      {isLoading ? (
        <CircularProgress />
      ) : (
        <>
          <Box
            display={'flex'}
            flexDirection={'row'}
            alignItems={'center'}
            justifyContent={'space-between'}
            gap={2}
          >
            <HeaderCard
              title={t('annual_budget')}
              value={`$${formatNumberToShortScale(initialTargetValue)}`}
            />

            <Divider
              orientation="horizontal"
              sx={{
                width: '2px',
                background: enableDarkTheme ? '#2F445D' : '#9E9E9E',
                height: '20px',
              }}
            />
            <HeaderCard
              title={t('rolling_1yr_actual')}
              value={`$${formatNumberToShortScale(totalCost)}`}
            />

            <Divider
              orientation="horizontal"
              sx={{
                width: '2px',
                background: enableDarkTheme ? '#2F445D' : '#9E9E9E',
                height: '20px',
              }}
            />

            <HeaderCard
              title={t('average_monthly_budget')}
              value={`$${formatNumberToShortScale(
                targetedAverageMonthlyMaintenanceCost
              )}`}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Typography
              sx={{
                fontSize: '14px',
                color: palette.textPrimary,
                fontWeight: 'bolder',
              }}
            >
              {t('monthly_maintenance_cost_distribution')}
            </Typography>
            <CustomLegend />
          </Box>

          <Box display={'flex'} flexDirection={'column'} gap={0}>
            <Box height={'300px'}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={chartData}
                  margin={{ top: 5, right: 5, left: 5, bottom: 0 }}
                  {...{
                    overflow: 'visible',
                  }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={palette.cartesianGrid}
                    vertical={false}
                  />

                  <XAxis
                    dataKey="name"
                    stroke={palette.textSecondary}
                    tick={{ fontSize: 10 }}
                    tickFormatter={(value: string) =>
                      value?.split(' ')[0]?.slice(0, 3)
                    }
                  />

                  <YAxis
                    domain={[0, chartMaxValue]}
                    label={{
                      value: t('maintenance_cost_axis'),
                      angle: -90,
                      position: 'insideLeft',
                      fontSize: '12px',
                    }}
                    stroke={palette.textSecondary}
                    tickFormatter={(value) => {
                      return value ? formatNumberToShortScale(value) : `0`;
                    }}
                    tick={{ fontSize: 10 }}
                  />

                  <Bar
                    dataKey="cost"
                    barSize={40}
                    radius={[5, 5, 0, 0]}
                    fill={palette.primary}
                    onClick={handleBarClick}
                    shape={(props: any) => (
                      <CustomRowHighlight
                        {...props}
                        chartWidth={props.width}
                        selectedCategory={selectedMonth}
                      />
                    )}
                    isAnimationActive={false}
                  >
                    <LabelList
                      dataKey={'cost'}
                      // position="right"
                      formatter={(v: any) => `$${formatNumberToShortScale(v)}`}
                      style={{
                        fontSize: 11,
                        fill: enableDarkTheme ? '#fff' : '#000',
                      }}
                      position={'top'}
                    />
                  </Bar>

                  <ReferenceLine
                    y={targetedAverageMonthlyMaintenanceCost}
                    stroke="#FFA403"
                    strokeWidth={3}
                  />

                  <Line
                    type="monotone"
                    dataKey="trend"
                    stroke="#E936EC"
                    strokeWidth={3}
                    dot={false}
                    activeDot={false}
                    strokeDasharray="3 3"
                  />

                  <Tooltip
                    cursor={{ fill: enableDarkTheme ? '#1B2B4A' : '#CCCCCC' }}
                    content={
                      <CustomTooltip
                        avgValue={targetedAverageMonthlyMaintenanceCost}
                      />
                    }
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
            <Typography
              sx={{
                fontSize: '10px ',
                py: '2px',
                color: palette.textPrimary,
              }}
            >
              {t('click_on_bar_to_see_cost_and_work_order_breakdown')}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};
